import React from "react";
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography, Box } from "@mui/material";

/**
 * Main / Second template switch for a YouTube tab whose brand has two
 * templates (2026-10-08). Renders nothing otherwise, so every other tab and
 * brand looks exactly as before. `pair` is `{ main, second, default }`
 * (hooks/templateSlots.js); `currentId` the template open now; `onPick(row)`.
 */
export default function TemplateSlotSwitch({ pair, currentId, onPick, busy = false, tone = "light" }) {
  if (!pair?.main || !pair?.second) return null;
  const current = [pair.main, pair.second].find((r) => String(r.template_id) === String(currentId ?? ""));
  const dark = tone === "dark";
  const label = (row) => row.template_name || row.template_id;
  const option = (row, slot) => (
    <ToggleButton
      value={slot}
      disabled={busy}
      data-testid={`template-slot-${slot}`}
      sx={{
        px: 1,
        py: 0,
        height: 26,
        fontSize: 11.5,
        fontWeight: 600,
        textTransform: "none",
        maxWidth: 170,
        color: dark ? "rgba(255,255,255,0.8)" : "#334155",
        borderColor: dark ? "rgba(255,255,255,0.3)" : "#e2e8f0",
        "&.Mui-selected": dark
          ? { color: "#1e1b4b", bgcolor: "white", "&:hover": { bgcolor: "white" } }
          : { color: "white", bgcolor: "#4338ca", "&:hover": { bgcolor: "#3730a3" } },
      }}
    >
      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label(row)}
        {row.is_default !== false && pair.default === row ? " · default" : ""}
      </Box>
    </ToggleButton>
  );
  return (
    <Box display="flex" alignItems="center" gap={0.75} data-testid="template-slot-switch">
      <Typography variant="caption" fontWeight={700} sx={{ color: dark ? "rgba(255,255,255,0.8)" : "#475569" }}>
        Template
      </Typography>
      <Tooltip title="This brand has two YouTube templates. Switching starts this tab over on the other one." disableInteractive>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={current ? (current === pair.main ? "main" : "second") : null}
          onChange={(_, slot) => slot && onPick(slot === "main" ? pair.main : pair.second)}
          aria-label="YouTube template"
        >
          {option(pair.main, "main")}
          {option(pair.second, "second")}
        </ToggleButtonGroup>
      </Tooltip>
    </Box>
  );
}
